import CloudDoneIcon from '@mui/icons-material/CloudDone'; // 🟢 Synchronisiert
import CloudSyncIcon from '@mui/icons-material/CloudSync'; // 🟡 Bearbeitet

import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useQueryClient } from "@tanstack/react-query";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from "axios";
import { Button, MenuItem, Select, TextField, Typography, Box, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useSessionStore, labelMap } from "./SessionStore"; // Zustand-Store für die aktuelle Sitzung
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import SessionDetails from './SessionDetails';
import { SettingsPhoneTwoTone } from '@mui/icons-material';
import { useSiteSettings } from './context/SiteSettingsContext';
import { useUsageStatus } from "./hooks/useUsageStatus";
import { useDialog } from './components/Modal/DialogContext';
import { useUser } from './context/UserContext';
import { transcribeDictation } from "./utils/dictation";
type ConfigureDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

type TemplateSource = "legacy" | "documentation_template" | "user_documentation_template";

type TemplateOption = {
  id: number;
  name: string;
  active: boolean;
  entity_type: string;
  for_intake?: boolean;
  for_progress?: boolean;
  for_biography?: boolean;
  for_summary?: boolean;
  for_individual?: boolean;
  for_couple?: boolean;
  for_group?: boolean;
  for_report?: boolean;
  for_dialogue?: boolean;
  for_vt?: boolean;
  for_st?: boolean;
  for_tp?: boolean;
  for_at?: boolean;
  template_source: TemplateSource;
  selection_key: string;
  has_user_overrides?: boolean;
  user_override_count?: number;
  template_detail_url?: string;
};

type ReportMaterialFile = {
  original_name: string;
  stored_name: string;
  display_markdown?: string;
  preview_text?: string;
  extracted_text?: string;
};

type ReportGender = "female" | "male" | "diverse" | "unknown";
type ReportType = "initial_report" | "conversion_report";

const ConfigureDialogContent: React.FC<ConfigureDialogProps> = ({ open, setOpen }) => {
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const dialog = useDialog();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const structuredTemplatesEnabled = !!siteSettings?.structured_templates_enabled;
  const customerTemplateGenerationMode = siteSettings?.customer_template_generation_mode ?? "legacy";
  const { user } = useUser();
  const isAdminOrInternal = !!(user?.is_staff || user?.is_admin_user || user?.is_internal);
  const canUseStructuredTemplates = !!(isAdminOrInternal || user?.has_template_dual_access || customerTemplateGenerationMode === "structured");
  const canUseLegacyTemplates = !!(isAdminOrInternal || user?.has_template_dual_access || customerTemplateGenerationMode !== "structured");
  const canUseBothTemplateSources = canUseStructuredTemplates && canUseLegacyTemplates;
  const sessionData = useSessionStore((state) => state.sessionData);
  const selectedSessionId = useSessionStore((state) => state.selectedSessionId);
  const fetchSessionDataById = useSessionStore((state) => state.fetchSessionDataById);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedNoteType, setSelectedNoteType] = useState("erstgespräch");
  const [selectedLocation, setSelectedLocation] = useState("Persönlich");
  const [selectedParticipation, setSelectedParticipation] = useState("einzel");
  const sessions = useSessionStore((s) => s.sessions);
  const currentSession = sessions.find((s) => s.id === selectedSessionId);
  const isCompleted = currentSession?.tempStatus === "completed";


  const [selectedGender, setSelectedGender] = useState<"male" | "female">("female");
  const [clientLabel, setClientLabel] = useState<"client" | "patient" | "customer">("client");
  type Gender = "male" | "female";
  type ClientLabel = "client" | "patient" | "customer";
  const clientLabelMap: Record<ClientLabel, Record<Gender, string>> = {
    client: { male: "Klient", female: "Klientin" },
    patient: { male: "Patient", female: "Patientin" },
    customer: { male: "Kunde", female: "Kundin" }
  };
  const [promptTemplates, setPromptTemplates] = useState<TemplateOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [llmModels, setLlmModels] = useState<string[]>([
    "meta-llama/Llama-3.3-70B-Instruct",
    "meta-llama/Meta-Llama-3.1-70B-Instruct",
    "mistralai/Mistral-7B-Instruct-v0.3",
    "meta-llama/Meta-Llama-3.1-8B-Instruct",
    "mistralai/Mixtral-8x7B-Instruct-v0.1",
  ]);
  const [selectedLlm, setSelectedLlm] = useState<string>("meta-llama/Llama-3.3-70B-Instruct");
  const [sessionName, setSessionName] = useState(sessionData?.name || "");
  const [therapyForm, setTherapyForm] = useState<"vt" | "st" | "tp" | "at" | null>(null);
  // Formular-Schritt: 1 = Name, 2 = Restliches Formular
  const [formStep, setFormStep] = useState<1 | 2>(1);
  const [selectedDate, setSelectedDate] = useState<Date | null>(sessionData?.date ? new Date(sessionData.date) : null);


  // Dynamische, editierbare Felder aus der Dokumentation

  const [currentInputIndex, setCurrentInputIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const audioChunksRef = useRef<Blob[]>([]);
  const professionalNotesChunksRef = useRef<Blob[]>([]);
  const professionalNotesRecorderRef = useRef<MediaRecorder | null>(null);
  const professionalNotesStreamRef = useRef<MediaStream | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewMaterial, setPreviewMaterial] = useState<ReportMaterialFile | null>(null);
  const [professionalNotes, setProfessionalNotes] = useState("");
  const [professionalNotesRecording, setProfessionalNotesRecording] = useState(false);
  const [professionalNotesProcessing, setProfessionalNotesProcessing] = useState(false);
  const [reportGender, setReportGender] = useState<ReportGender>("unknown");
  const [reportType, setReportType] = useState<ReportType>("initial_report");
  const [previousSessionCount, setPreviousSessionCount] = useState("");
  const [requestedSessionCount, setRequestedSessionCount] = useState("");
  const queryClient = useQueryClient();

  const { data: usageStatus, isLoading: isUsageLoading } = useUsageStatus();


  useEffect(() => {
    axios.get("/api/apps/note-settings")
      .then((res) => {
        if (res.data.client_label) {
          setClientLabel(res.data.client_label);
        }
        if (res.data.therapy_form) {
          setTherapyForm(res.data.therapy_form);
        }
      })
      .catch((err) => {
        console.error("Fehler beim Laden des client_label:", err);
      });
  }, []);


  useEffect(() => {
    // Immer den sessionName resetten, auch wenn leer
    setSessionName(sessionData?.name ?? "");
    setFormStep(sessionData?.name ? 2 : 1);

    if (sessionData?.gender) {
      setSelectedGender(sessionData.gender === "female" ? "female" : "female");
    }


    if (sessionData?.session_type) {
      if (sessionData.session_type === "progress_note") {
        setSelectedNoteType("verlaufsdokumentation");
      } else if (sessionData.session_type === "intake") {
        setSelectedNoteType("erstgespräch");
      } else if (sessionData.session_type === "biography") {
        setSelectedNoteType("biografie");
      } else if (sessionData.session_type === "summary") {
        setSelectedNoteType("zusammenfassung");
      }
    }

    if (sessionData?.participation_type) {
      const participationMap: Record<string, string> = {
        individual: "Einzel",
        couple: "Paar",
        group: "Gruppe",
      };
      setSelectedParticipation(participationMap[sessionData.participation_type] || "Einzel");
    }
    if (sessionData?.location) {
      const locationMap: Record<string, string> = {
        in_person: "Persönlich",
        online: "Online",
        clients_home: "Hausbesuch",
      };
      setSelectedLocation(locationMap[sessionData.location] || "Persönlich");
    }

    if (sessionData?.date) {
      setSelectedDate(new Date(sessionData.date));
    } else {
      setSelectedDate(null);
    }

    const reportMeta = sessionData?.data?.ReportMeta || {};
    const metaGender = reportMeta.gender;
    setReportGender(
      metaGender === "female" || metaGender === "male" || metaGender === "diverse" || metaGender === "unknown"
        ? metaGender
        : "unknown"
    );
    const metaReportType = reportMeta.report_type;
    setReportType(
      metaReportType === "initial_report" || metaReportType === "conversion_report"
        ? metaReportType
        : "initial_report"
    );
    setPreviousSessionCount(
      reportMeta.previous_session_count === null || reportMeta.previous_session_count === undefined
        ? ""
        : String(reportMeta.previous_session_count)
    );
    setRequestedSessionCount(
      reportMeta.requested_session_count === null || reportMeta.requested_session_count === undefined
        ? ""
        : String(reportMeta.requested_session_count)
    );
    setProfessionalNotes(typeof reportMeta.professional_notes === "string" ? reportMeta.professional_notes : "");
  }, [sessionData, selectedSessionId]);

  useEffect(() => {
    return () => {
      if (professionalNotesRecorderRef.current?.state === "recording") {
        professionalNotesRecorderRef.current.stop();
      }
      professionalNotesStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  useEffect(() => {
    const filtered = getFilteredTemplates();
    if (filtered.length > 0) {
      setSelectedTemplate((currentSelection) => {
        if (currentSelection && filtered.some((tpl) => tpl.selection_key === currentSelection)) {
          return currentSelection;
        }
        return filtered[0].selection_key;
      });
    } else if (selectedTemplate !== "") {
      setSelectedTemplate("");
    }
  }, [selectedNoteType, selectedParticipation, promptTemplates, therapyForm, selectedTemplate]);


  useEffect(() => {
    const fetchPromptTemplates = async () => {
      try {
        const requests = [axios.get("/api/apps/fetch-templates/")];
        if (structuredTemplatesEnabled) {
          requests.push(axios.get("/api/apps/fetch-documentation-templates/"));
          if (canUseStructuredTemplates) {
            requests.push(
              axios.get("/api/apps/user-templates/").catch((error) => {
                console.warn("UserTemplates konnten nicht geladen werden:", error);
                return { data: [] } as any;
              })
            );
          }
        }
        const [legacyResponse, structuredResponse, userTemplatesResponse] = await Promise.all(requests);
        const legacyTemplates: TemplateOption[] = (legacyResponse.data || []).map((tpl: any) => ({
          ...tpl,
          template_source: "legacy",
          selection_key: `legacy:${tpl.id}`,
        }));
        const structuredTemplates: TemplateOption[] = structuredTemplatesEnabled
          ? ((structuredResponse?.data || []) as any[]).map((tpl) => ({
              ...tpl,
              template_source: "documentation_template",
              selection_key: `documentation:${tpl.id}`,
            }))
          : [];
        const userTemplates: TemplateOption[] = structuredTemplatesEnabled && canUseStructuredTemplates
          ? ((userTemplatesResponse?.data || []) as any[]).map((tpl) => {
              const hasAnyTherapyFlag = !!(tpl.for_vt || tpl.for_st || tpl.for_tp || tpl.for_at);
              return {
                ...tpl,
                name: tpl.name || tpl.title,
                template_source: "user_documentation_template",
                selection_key: `user:${tpl.id}`,
                for_vt: hasAnyTherapyFlag ? tpl.for_vt : true,
                for_st: hasAnyTherapyFlag ? tpl.for_st : true,
                for_tp: hasAnyTherapyFlag ? tpl.for_tp : true,
                for_at: hasAnyTherapyFlag ? tpl.for_at : true,
              };
            })
          : [];
        setPromptTemplates([
          ...(canUseLegacyTemplates ? legacyTemplates : []),
          ...structuredTemplates,
          ...userTemplates,
        ]);
      } catch (error) {
        console.error("Fehler beim Laden der PromptTemplates:", error);
      }
    };

    fetchPromptTemplates();
  }, [structuredTemplatesEnabled, canUseStructuredTemplates, canUseLegacyTemplates]);


  const getFilteredTemplates = () => {
    if (!Array.isArray(promptTemplates) || promptTemplates.length === 0) return [];

    const noteType = selectedNoteType?.toLowerCase() ?? "";
    const participation = selectedParticipation?.toLowerCase() ?? "";
    const transcriptType = sessionData?.transcript_type ?? "";

    return promptTemplates.filter((tpl) => {
      if (tpl.entity_type !== "case") return false;
      // Filter für inaktive Templates
      if (!tpl.active) return false;
      if (noteType === "verlaufsdokumentation" && !tpl.for_progress) return false;
      if (noteType === "erstgespräch" && !tpl.for_intake) return false;
      if (noteType === "biografie" && !tpl.for_biography) return false;
      if (noteType === "zusammenfassung" && !tpl.for_summary) return false;

      if (participation === "paar" && !tpl.for_couple) return false;
      if (participation === "gruppe" && !tpl.for_group) return false;
      if (participation === "einzel" && !tpl.for_individual) return false;

      if (transcriptType === "dialogue" && !tpl.for_dialogue) return false;
      if (transcriptType === "report" && !tpl.for_report) return false;
      // Therapieform-Filter ergänzen
      if (therapyForm) {
        if (therapyForm === "vt" && !tpl.for_vt) return false;
        if (therapyForm === "st" && !tpl.for_st) return false;
        if (therapyForm === "tp" && !tpl.for_tp) return false;
        if (therapyForm === "at" && !tpl.for_at) return false;
      }
      return true;
    });
  };

  const selectedTemplateObject = promptTemplates.find((tpl) => tpl.selection_key === selectedTemplate) || null;

  const appendProfessionalNotes = (text: string) => {
    const cleanedText = text.trim();
    if (!cleanedText) return;
    setProfessionalNotes((previous) => {
      const previousText = previous.trim();
      return previousText ? `${previousText}\n${cleanedText}` : cleanedText;
    });
  };

  const toggleProfessionalNotesDictation = async () => {
    if (professionalNotesRecording) {
      professionalNotesRecorderRef.current?.stop();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      professionalNotesStreamRef.current = stream;
      professionalNotesChunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      professionalNotesRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          professionalNotesChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        setProfessionalNotesRecording(false);
        setProfessionalNotesProcessing(true);
        professionalNotesStreamRef.current?.getTracks().forEach((track) => track.stop());
        professionalNotesStreamRef.current = null;

        try {
          const audioBlob = new Blob(professionalNotesChunksRef.current, { type: "audio/webm" });
          const transcript = await transcribeDictation(audioBlob);
          appendProfessionalNotes(transcript);
        } catch (error) {
          console.error("Fehler beim Transkribieren der fachlichen Hinweise:", error);
          alert("Diktat konnte nicht transkribiert werden.");
        } finally {
          professionalNotesChunksRef.current = [];
          setProfessionalNotesProcessing(false);
        }
      };

      recorder.start();
      setProfessionalNotesRecording(true);
    } catch (error) {
      console.error("Mikrofonzugriff für fachliche Hinweise fehlgeschlagen:", error);
      alert("Mikrofon konnte nicht gestartet werden.");
    }
  };

  const buildReportMetaPayload = () => ({
    gender: reportGender,
    report_type: reportType,
    previous_session_count: previousSessionCount,
    requested_session_count: requestedSessionCount,
    professional_notes: professionalNotes,
  });

  // Funktion zum Senden der Dokumentationsanforderung
  const handleGenerateDocumentation = async () => {
    if (!selectedTemplate) {
      alert("Bitte ein Prompt-Template auswählen!");
      return;
    }
    if (!sessionData || !sessionData.id) {
      alert("Keine Sitzung ausgewählt!");
      return;
    }
    if (!selectedTemplateObject) {
      alert("Das ausgewählte Template konnte nicht gefunden werden.");
      return;
    }

    setLoading(true);

    try {
      // Update der Session vor der Generierung
      await axios.put("/api/apps/update-session/", {
        id: sessionData.id,
        gender: selectedGender === "female" ? "female" : "male",
        session_type:
          selectedNoteType === "verlaufsdokumentation"
            ? "progress_note"
            : selectedNoteType === "biografie"
              ? "biography"
              : selectedNoteType === "zusammenfassung"
                ? "summary"
                : "intake",
        location:
          selectedLocation === "Persönlich"
            ? "in_person"
            : selectedLocation === "Online"
              ? "online"
              : "clients_home",
        name: sessionName,
        participation_type:
          selectedParticipation === "Einzel"
            ? "individual"
            : selectedParticipation === "Paar"
              ? "couple"
              : "group",
        date:
          selectedDate &&
            sessionData?.date &&
            selectedDate.toDateString() !== new Date(sessionData.date).toDateString()
            ? new Date(
              selectedDate.getFullYear(),
              selectedDate.getMonth(),
              selectedDate.getDate(),
              new Date(sessionData.date).getHours(),
              new Date(sessionData.date).getMinutes(),
              new Date(sessionData.date).getSeconds()
            ).toISOString()
            : undefined,
        template_used: selectedTemplateObject.name || "",
      });

      if (selectedTemplateObject.template_source === "documentation_template") {
        await axios.post("/api/services/generate-documentation-template/", {
          user_sitzung_id: sessionData.id,
          documentation_template_id: selectedTemplateObject.id,
          supplements_text: professionalNotes,
          report_meta: buildReportMetaPayload(),
        });
      } else if (selectedTemplateObject.template_source === "user_documentation_template") {
        await axios.post("/api/services/generate-documentation-template/", {
          user_sitzung_id: sessionData.id,
          template_source: "user_template",
          user_documentation_template_id: selectedTemplateObject.id,
          supplements_text: professionalNotes,
          report_meta: buildReportMetaPayload(),
        });
      } else {
        await axios.post("/api/services/generate-documentation/", {
          user_sitzung_id: sessionData.id,
          prompt_template_id: selectedTemplateObject.id,
          llm_model: selectedLlm,
          supplements_text: professionalNotes,
          report_meta: buildReportMetaPayload(),
        });
      }
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["usage-status"] });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        dialog({
          type: "alert",
          title: "Hinweis",
          message: error.response?.data?.error || "Maximal 4 Dokumentationen erlaubt. Bitte zuerst eine löschen.",
        });
        return;
      }
      console.error("Fehler beim Generieren der Dokumentation:", error);
      alert("Fehler beim Generieren der Dokumentation.");
    } finally {
      setLoading(false);
    }
  };

  // Entfernen-Funktion für Dateien
  const handleRemoveFile = async (file: { stored_name: string }) => {
    if (!sessionData?.id) return;

    try {
      // Backend-Request, um die Datei zu löschen
      await axios.delete("/api/services/delete-document/", {
        data: {
          session_id: sessionData.id,
          stored_name: file.stored_name
        }
      });
      await fetchSessionDataById(sessionData.id);

    } catch (error) {
      console.error("Fehler beim Entfernen der Datei:", error);
      alert("Fehler beim Entfernen der Datei.");
    }
  };

  const getMaterialPreviewText = (file: ReportMaterialFile | null) => {
    if (!file) return "";
    return file.display_markdown || file.preview_text || file.extracted_text || "Für dieses Material ist keine Vorschau verfügbar.";
  };

  // Neue Funktion zum Hinzufügen von Dateien
  const handleAddFiles = async (files: FileList) => {
    if (!sessionData?.id) return;

    const formData = new FormData();
    Array.from(files).forEach(file => formData.append("files", file));
    formData.append("session_id", String(sessionData.id));

    try {
      const response = await axios.post("/api/services/add-documents/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Nach erfolgreichem Upload: Session-Daten neu laden
      console.log("Dateien erfolgreich hinzugefügt:", response.data);
      await fetchSessionDataById(sessionData.id);
    } catch (error) {
      console.error("Fehler beim Hinzufügen der Datei:", error);
      alert("Fehler beim Hochladen der Datei(en).");
    }
  };

  return (
    <div className="upload-form__container__main">
      <fieldset disabled={!isCompleted || loading} style={{ border: "none", padding: 0, margin: 0 }}>
        <div className="upload-form__container__main__widget">
          {/* Schritt 1: Nur Name/Titel */}
          {formStep === 1 && (
            <>
              <div id="note-configure-name-1" className="upload-form__container__main__widget__session__timer">
                <div className="_Section_17y3r_1">
                  <label className="_Label_17y3r_18 !tw-font-normal !tw-text-[12px] tw-text-[#040404]">Name/ Titel</label>
                  <div className="_ClockContainer_17y3r_24">
                    <div className="_CustomInput_17y3r_32 _CustomInputWithClassName_17y3r_131">
                      <input
                        type="text"
                        className="_Value_17y3r_56"
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          font: "inherit",
                          padding: 0,
                        }}
                        value={sessionName}
                        onChange={(e) => setSessionName(e.target.value)}
                        placeholder="Titel eingeben"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="upload-form__container__main__widget__button">
                <button
                  type="button"
                  className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 !bg-[#E7A555]"
                  disabled={!sessionName.trim()}
                  onClick={async () => {
                    if (!sessionData?.id) return;
                    setLoading(true);
                    try {
                      await axios.put("/api/apps/update-session/", {
                        id: sessionData.id,
                        name: sessionName,
                      });
                      setFormStep(2);
                    } catch (e) {
                      console.error("Fehler beim Speichern des Namens", e);
                      alert("Fehler beim Speichern.");
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  <div className="_btnFlex_19rto_32">{loading ? "Speichern..." : "Speichern und weiter"}</div>
                </button>
              </div>
            </>
          )}
          {/* Schritt 2: Restliches Formular */}
          {formStep === 2 && (
            <>
              <div id="note-configure-name-2" className="upload-form__container__main__widget__session__timer">
                <div className="_Section_17y3r_1">
                  <label className="_Label_17y3r_18 !tw-font-normal !tw-text-[12px] tw-text-[#040404]">Name/ Titel</label>
                  <div className="_ClockContainer_17y3r_24">
                    <div className="_CustomInput_17y3r_32 _CustomInputWithClassName_17y3r_131">
                      <input
                        type="text"
                        className="_Value_17y3r_56"
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          font: "inherit",
                          padding: 0,
                        }}
                        value={sessionName}
                        onChange={(e) => setSessionName(e.target.value)}
                        placeholder="Titel eingeben"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Dateiliste: Vorhandene Dokumente (scrollbar, Upload-TSX-Style) */}
              {Array.isArray(sessionData?.data?.Files) && sessionData.data.Files.length > 0 && (
                <div id="note-configure-name-2" className="upload-form__container__main__widget__session__timer">
                  <div className="_Section_17y3r_1">
                    <div className="_nameWrapperFlex_16d6j_37">
                      <div className="_nameWrapper_16d6j_37">
                        <div className="_fileName_16d6j_51">
                          <div className="_firstChunk_16d6j_61">Vorhandene Dokumente (</div>
                          <div className="_lastChunk_16d6j_66">
                            {sessionData.data.Files.length} Datei{sessionData.data.Files.length === 1 ? '' : 'en'})
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="tw-flex-1 tw-flex tw-items-center">
                      <div className="tw-w-full">
                        <div className="_previews_1aueu_187">
                          <div className="tw-border tw-rounded-lg tw-p-2 tw-bg-white">
                            <div
                              className="tw-text-left tw-space-y-2"
                              style={{ maxHeight: '240px', overflowY: 'auto' }}
                            >
                              {sessionData.data.Files.map((file: ReportMaterialFile, idx) => (
                                <div
                                  key={idx}
                                  className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-py-1 tw-px-2 tw-bg-gray-50 tw-rounded-md"
                                >
                                  <span className="tw-text-sm tw-truncate">{file.original_name}</span>
                                  <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                                    <button
                                      type="button"
                                      className="tw-text-green-800 tw-text-sm tw-whitespace-nowrap hover:tw-text-green-600"
                                      onClick={() => setPreviewMaterial(file)}
                                    >
                                      Dokument anschauen
                                    </button>
                                    {sessionData?.data?.Files.length > 1 && (
                                      <button
                                        type="button"
                                        className="tw-text-red-500 tw-text-sm tw-w-20 tw-flex-shrink-0 tw-text-center"
                                        onClick={() => handleRemoveFile(file)}
                                      >
                                        Entfernen
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>{/* Button "+ Dateien hinzufügen" unterhalb der Dateiliste */}
                            <div className="tw-mt-2 tw-text-center">
                              <button
                                type="button"
                                className="text-green-800 text-sm hover:text-green-600"
                                onClick={() => document.getElementById("file-upload-input")?.click()}
                              >
                                + Dokument(e) hinzufügen
                              </button>
                              <input
                                id="file-upload-input"
                                type="file"
                                multiple
                                accept=".txt,.pdf,.docx"
                                style={{ display: "none" }}
                                onChange={(e) => e.target.files && handleAddFiles(e.target.files)}
                              />
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div id="report-professional-notes" className="upload-form__container__main__widget__session__timer">
                <div className="_Section_17y3r_1">
                  <label className="_Label_17y3r_18 !tw-font-normal !tw-text-[12px] tw-text-[#040404]">
                    Zusätzliche fachliche Hinweise (optional)
                  </label>
                  <div className="_ClockContainer_17y3r_24">
                    <div className="_CustomInput_17y3r_32 _CustomInputWithClassName_17y3r_131 tw-relative">
                      <textarea
                        className="_Value_17y3r_56 placeholder:text-xs"
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          font: "inherit",
                          padding: 0,
                          minHeight: "48px",
                          resize: "vertical",
                        }}
                        value={professionalNotes}
                        onChange={(event) => setProfessionalNotes(event.target.value)}
                        placeholder="Wichtige fachliche Hinweise für den Bericht ..."
                      />
                      <button
                        type="button"
                        className="tw-absolute tw-right-2 tw-bottom-2 tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-green-800 hover:tw-text-green-600"
                        onClick={toggleProfessionalNotesDictation}
                        disabled={professionalNotesProcessing}
                      >
                        {professionalNotesRecording ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                        {professionalNotesRecording
                          ? "Stoppen"
                          : professionalNotesProcessing
                            ? "Verarbeite..."
                            : "Diktat"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div id="report-meta" className="upload-form__container__main__widget__session__timer">
                <div className="_Section_17y3r_1">
                  <label className="_Label_17y3r_18 !tw-font-normal !tw-text-[12px] tw-text-[#040404]">
                    Berichtsdaten
                  </label>
                  <div className="tw-space-y-4 tw-w-full">
                    <div>
                      <div className="tw-text-sm tw-text-gray-700 tw-mb-2">Anrede/Geschlecht</div>
                      <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                        {[
                          ["female", "weiblich"],
                          ["male", "männlich"],
                          ["diverse", "divers"],
                          ["unknown", "unbekannt"],
                        ].map(([value, label]) => (
                          <label key={value} className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                            <input
                              type="radio"
                              name="report-gender"
                              value={value}
                              checked={reportGender === value}
                              onChange={() => setReportGender(value as ReportGender)}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="tw-text-sm tw-text-gray-700 tw-mb-2">Art des Berichts</div>
                      <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                        {[
                          ["initial_report", "Erstbericht"],
                          ["conversion_report", "Umwandlungsbericht"],
                        ].map(([value, label]) => (
                          <label key={value} className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                            <input
                              type="radio"
                              name="report-type"
                              value={value}
                              checked={reportType === value}
                              onChange={() => setReportType(value as ReportType)}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                      <label className="tw-flex tw-flex-col tw-gap-1 tw-text-sm">
                        Sitzungsanzahl bisher
                        <input
                          type="number"
                          min="0"
                          className="tw-border tw-rounded tw-px-2 tw-py-1"
                          value={previousSessionCount}
                          onChange={(event) => setPreviousSessionCount(event.target.value)}
                        />
                      </label>
                      <label className="tw-flex tw-flex-col tw-gap-1 tw-text-sm">
                        Sitzungsanzahl beantragt
                        <input
                          type="number"
                          min="0"
                          className="tw-border tw-rounded tw-px-2 tw-py-1"
                          value={requestedSessionCount}
                          onChange={(event) => setRequestedSessionCount(event.target.value)}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/*  <div className="upload-form__container__main__widget__session__timer">
                        <div className="_Section_17y3r_1">
                          <label className="_Label_17y3r_18 !tw-font-normal !tw-text-[12px] tw-text-[#040404]">Dauer der Sitzung</label>
                          <div className="_ClockContainer_17y3r_24">
                            <div className="_CustomInput_17y3r_32 _CustomInputWithClassName_17y3r_131">
                              <div className="tw-space-x-2"><span className="_Value_17y3r_56">{sessionData?.length || "N/A"}</span></div>
                            </div>
                          </div>
                        </div>
                      </div>*/}
              

              {/* <div className="upload-form__container__main__widget__session !tw-flex">
                      <div className="upload-form__container__main__widget__name">
                        <label className="label tw-flex tw-gap-[0.1rem] !tw-font-normal !tw-mb-2">Client <span className="!tw-text-danger-500 tw-m-0">*</span></label>
                        <div className="css-b62m3t-container">
                          <div className="css-1m90hyn-control">
                            <div className="css-hlgwow">
                              <div className="css-1dimb5e-singleValue">
                                <div className="tw-flex tw-items-center tw-gap-2 tw-justify-between ">
                                  <div className="tw-text-sm tw-capitalize"><span className="tw-m-0 tw-flex">Lucy (example client)</span></div>
                                  <div className="tw-flex tw-items-center tw-align-center tw-gap-2">
                                    <div className="_badge_1ngyc_1 _badge-individual_1ngyc_54 tw-p-2">individual</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="add-new-client tw-relative">
                          <img className="client_img" src="path/to/add-icon.svg" alt="add new client" />
                          <p className="client_text">Add new client</p>
                        </div>
                      </div>
                    </div>*/}
              <div id="note-configure-templates" className="selector__container">
                <div className="selector__container__main">
                  <label className="label !tw-font-normal">Template</label>
                  <div className="selector__container__create">
                    <div className="note__types">
                      <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                        <div className="tw-flex-1">
                          <select
                            className="note__types__span note-selected tw-w-full"
                            value={selectedTemplate ?? ""}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                          >
                            {getFilteredTemplates().length === 0 ? (
                              <option value="">Kein passendes Template gefunden</option>
                            ) : (
                              getFilteredTemplates().map((tpl, idx) => (
                                <option key={tpl.selection_key || idx} value={tpl.selection_key}>
                                  {tpl.name || `Template ${idx + 1}`}{tpl.template_source === "documentation_template" && tpl.has_user_overrides ? "\u00A0\u00A0•\u00A0\u00A0angepasst" : ""}{canUseBothTemplateSources && tpl.template_source === "documentation_template" ? " [Structured]" : tpl.template_source === "user_documentation_template" ? "\u00A0\u00A0•\u00A0\u00A0eigene Vorlage" : ""}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  {selectedTemplateObject?.template_source === "documentation_template" && selectedTemplateObject.has_user_overrides ? (
                    <div className="mt-2 tw-text-[12px] tw-text-gray-600">
                      Diese Vorlage enthält persönliche Anpassungen.{" "}
                      <Link className="tw-font-medium tw-text-gray-700 tw-underline" to={selectedTemplateObject.template_detail_url || `/my-templates/${selectedTemplateObject.id}`}>
                        Vorlage öffnen
                      </Link>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="upload-form__container__main__widget__button">
                <button
                  type="button"
                  className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 !bg-[#E7A555]"
                  onClick={handleGenerateDocumentation}
                  disabled={
                    !isCompleted ||
                    loading ||
                    getFilteredTemplates().length === 0 ||
                    (!isUsageLoading && usageStatus && !usageStatus.has_notes_left)
                  }
                >
                  <div className="_btnFlex_19rto_32">
                    {!isCompleted || loading
                      ? "Generiere..."
                      : !isUsageLoading && usageStatus && !usageStatus.has_notes_left
                        ? "Limit erreicht"
                        : "Generiere Dokumentation"}
                  </div>
                </button>

                {!isUsageLoading && usageStatus && !usageStatus.has_notes_left && (
                  <p className="tw-text-sm tw-text-gray-600 tw-mt-2 tw-text-center">
                    Sie haben Ihr Dokumentationslimit erreicht.{" "}
                    <Link
                      to="/settings/planpicker"
                      className="tw-text-primary-600 tw-underline hover:tw-text-primary-800"
                    >
                      Jetzt Plan buchen oder upgraden
                    </Link>
                    , um mehr Dokumentationen zu erstellen.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </fieldset>
      <Dialog
        open={!!previewMaterial}
        onClose={() => setPreviewMaterial(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{previewMaterial?.original_name || "Dokumentvorschau"}</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              maxHeight: "60vh",
              overflow: "auto",
              backgroundColor: "#fff",
              wordBreak: "break-word",
              fontSize: "0.9rem",
              lineHeight: 1.6,
              "& h1": { fontSize: "1.35rem", fontWeight: 700, margin: "0 0 0.75rem" },
              "& h2": { fontSize: "1.15rem", fontWeight: 700, margin: "1rem 0 0.5rem" },
              "& h3": { fontSize: "1rem", fontWeight: 700, margin: "0.75rem 0 0.4rem" },
              "& p": { margin: "0 0 0.75rem" },
              "& ul, & ol": {
                paddingLeft: "1.4rem",
                margin: "0 0 0.75rem",
                backgroundColor: "#fff",
              },
              "& li": {
                marginBottom: "0.25rem",
                backgroundColor: "#fff",
              },
              "& li:hover": {
                backgroundColor: "#fff",
              },
              "& pre": {
                whiteSpace: "pre-wrap",
                backgroundColor: "#f7f7f7",
                padding: "0.75rem",
                borderRadius: "6px",
                overflow: "auto",
              },
              "& code": {
                backgroundColor: "#f7f7f7",
                padding: "0.1rem 0.25rem",
                borderRadius: "4px",
              },
            }}
          >
            <ReactMarkdown>{getMaterialPreviewText(previewMaterial)}</ReactMarkdown>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewMaterial(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
export default ConfigureDialogContent;
